import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Location } from 'react-router-dom'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import api from '@disco/api/server'
import { isTaxNotCalculated } from '@disco/state-management/selectors/subscription'
import BillingInformation from 'components/billing-information'
import ModalActionDialog from 'components/modals/dialogs/modal-action-dialog'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import styles from './index.module.css'

const UpdateBillingInformationModal = (): React.ReactNode => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [confirmed, setConfirmed] = useState(false)
  const [billingInformation, setBillingInformation] = useState<{
    form: Record<string, any>
    confirm: () => void
  }>({
    form: {},

    confirm() {},
  })
  const [updateModal, setUpdateModal] = useState<React.ReactElement | null>(
    null,
  )
  const [sendFormWaiting, setSendFormWaiting] = useState(false)
  const subscription = useSelector(state => state.subscription)
  const user = useSelector(state => state.user)
  const taxNotCalculated = useSelector(isTaxNotCalculated)
  const disabledUpdatePaymentDetails = !user.permissions?.includes(
    'can_change_business_settings',
  )
  const isBankDraftSubscriber =
    user.business?.private_metadata?.plan_type === 'bank_draft_subscriber'

  const reset = () => {
    setUpdateModal(null)
  }
  const close = () => {
    const returnPath = location.state?.returnTo ?? '/settings/billing'
    navigate(returnPath)
  }

  const errModal = (
    <ModalActionDialog
      type="error"
      header="Sorry!"
      submitButtonCaption="Ok"
      onSubmit={reset}
      onClose={reset}
    >
      Sorry, that billing contact information couldn&#39;t be added.
    </ModalActionDialog>
  )
  const successModal = (
    <ModalActionDialog
      type="success"
      header="Success!"
      submitButtonCaption="Ok"
      onSubmit={close}
      onClose={close}
    >
      Thanks! Your billing contact information have been updated.
    </ModalActionDialog>
  )

  const onChange = ({ form, confirm }) => {
    setBillingInformation({
      form,
      confirm,
    })
  }

  const onEdit = () => {
    setConfirmed(false)
  }

  const onSubmit = e => {
    e.preventDefault()
    billingInformation.confirm()
    setConfirmed(true)

    if (confirmed) {
      submitChargebeeData()
    }
  }

  const submitChargebeeData = async () => {
    setSendFormWaiting(true)
    try {
      await api.updateSubscriptionCustomer(billingInformation.form)

      if (!subscription.loading && !isBankDraftSubscriber) {
        dispatch(loadBusinessSubscription())
      }

      setUpdateModal(successModal)
    } catch (error) {
      setUpdateModal(errModal)
    }
    setSendFormWaiting(false)
  }

  useEffect(() => {
    if (user && disabledUpdatePaymentDetails) {
      close()
    }
  }, [user])

  if (updateModal) {
    return updateModal
  }

  return (
    <ModalWindow
      header={<Heading>Update Billing contact information</Heading>}
      isOpen
      width="medium"
      onClose={close}
    >
      <form onSubmit={onSubmit}>
        {taxNotCalculated && (
          <div className={styles.updateBillingInformationModalAlertContainer}>
            <Alert icon="warning" status="warning">
              To calculate sales tax, we need you to update your address. Please
              add your address below.
              <div
                className={styles.updateBillingInformationModalLinkContainer}
              >
                <a
                  className="text-link"
                  href="https://support.disco.ac/home/information-on-sales-tax-charges-for-united-states-users"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  More information on sales tax
                </a>
              </div>
            </Alert>
          </div>
        )}
        <BillingInformation onChange={onChange} onEdit={onEdit} />

        <Button
          appearance="primary"
          caption={confirmed ? 'Confirm details' : 'Update'}
          progressCaption="Updating"
          progressIndicator={sendFormWaiting}
          type="submit"
        />
      </form>
    </ModalWindow>
  )
}

export default UpdateBillingInformationModal
